import { useDesignatedDonation } from '@features/checkout/hooks/useDesignatedDonation'
import { needsQK } from '@features/needs/model/legacy'
import { OrgKeys, orgKeys } from '@features/organizations/model/query-keys'
import { userQK } from '@features/profile/model/userQK'
import { useRoadblockAction } from '@features/roadblock'
import { useMutationWithAuth } from '@lib/Auth/clerk-utils'
import type { MutationContext, SnapshotDetail } from '@lib/react-query'
import type {
  FollowOrganizationMutation,
  OrganizationByAliasQuery,
  UnfollowOrganizationMutation,
  UserConnectionsQuery,
} from '@purposity/sdk'
import type { IdWithAlias } from '@purposity/utils'
import { analytics } from '@services/Analytics'
import { sdk } from '@services/sdk'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import type { ClientError } from 'graphql-request'
import produce from 'immer'
import { useDebugValue } from 'react'
import { useUserConnectionsQuery } from '../../connections/useConnections'
import { useActionParamsFromAlias } from './useOrganizations'

type OrgActionMutationContext = MutationContext<{
  connections?: SnapshotDetail<
    ReturnType<typeof userQK.connections>,
    UserConnectionsQuery
  >
  orgDetails?: SnapshotDetail<
    OrgKeys['detail']['queryKey'],
    OrganizationByAliasQuery
  >
}>

export function useOrgActions(params: { aliasOrValias: string }) {
  const actionParams = useActionParamsFromAlias(params.aliasOrValias, true)

  const { follow, _mutation: _follow } = useFollowOrg(actionParams)
  const { unfollow, _mutation: _unfollow } = useUnfollowOrg(actionParams)

  const isFollower = useIsOrgSubscriber({
    v_alias: actionParams.v_alias,
  })

  const { donate, ..._donate } = useDesignatedDonation({
    type: 'org',
    aliasOrValias: params.aliasOrValias,
  })

  useDebugValue({ isFollower, follow, unfollow, donate })

  return {
    isFollower,
    follow,
    unfollow,
    donate,
    _donate,
    _follow,
    _unfollow,
  }
}

function useFollowOrg(org: IdWithAlias) {
  const mutation = useFollowOrgMutation()
  const { callback } = useRoadblockAction({
    trigger: () => mutation.mutate(org),
    referral: {
      entity: 'org',
      method: 'follow',
      params: { orgId: org.id },
    },
  })
  return { follow: callback, _mutation: mutation }
}

function useUnfollowOrg(org: IdWithAlias) {
  const mutation = useUnfollowOrgMutation()
  const { callback } = useRoadblockAction({
    trigger: () => mutation.mutate(org),
    referral: {
      entity: 'org',
      method: 'unfollow',
      params: { orgId: org.id },
    },
  })
  return { unfollow: callback, _mutation: mutation }
}

export function useFollowOrgMutation() {
  const queryClient = useQueryClient()

  return useMutationWithAuth<
    FollowOrganizationMutation,
    ClientError,
    IdWithAlias,
    OrgActionMutationContext
  >({
    async mutationFn(variables) {
      return sdk.followOrganization({ orgId: variables.id }, this.meta?.headers)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries(userQK.connections())

      const { snapshot } = createSnapshot(queryClient, variables)

      queryClient.setQueryData<UserConnectionsQuery>(
        userQK.connections(),
        (prevData = { me: { orgs: [], lists: [], teams: [] } }) => ({
          me: {
            orgs: [{ org: variables }, ...(prevData.me?.orgs || [])],
            lists: prevData.me?.lists || [],
            teams: prevData.me?.teams || [],
          },
        })
      )

      queryClient.setQueryData<OrganizationByAliasQuery>(
        orgKeys.detail(variables.v_alias).queryKey,
        ({ organization } = { organization: [] }) => ({
          organization: produce(organization, (draft) => {
            if (draft[0]?.stats?.follower_count) draft[0].stats.follower_count++
            return draft
          }),
        })
      )

      return {
        snapshot,
      }
    },

    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        Object.values(context.snapshot).forEach((item) => {
          queryClient.setQueryData(item.key, item.data)
        })
      }
    },

    onSuccess: (_response, variables) => {
      analytics.track('Organization Follow', {
        id: variables.id,
        orgName: variables.alias,
      })
    },

    onSettled: (_response, _error, variables) =>
      Promise.all([
        queryClient.invalidateQueries(userQK.connections()),
        queryClient.invalidateQueries(orgKeys.detail(variables.alias)),
        queryClient.invalidateQueries(needsQK.list({ slug: 'subscribed' })),
      ]),
  })
}

function useUnfollowOrgMutation() {
  const queryClient = useQueryClient()

  return useMutationWithAuth<
    UnfollowOrganizationMutation,
    ClientError,
    IdWithAlias,
    OrgActionMutationContext
  >({
    async mutationFn(variables) {
      return sdk.unfollowOrganization(
        { orgId: variables.id },
        this.meta?.headers
      )
    },

    async onMutate(variables) {
      await queryClient.cancelQueries(userQK.connections())

      const { snapshot } = createSnapshot(queryClient, variables)

      queryClient.setQueryData<UserConnectionsQuery>(
        userQK.connections(),
        (prevData) => ({
          me: {
            orgs:
              prevData?.me?.orgs.filter(
                (membership) => membership.org?.id !== variables.id
              ) || [],
            lists: prevData?.me?.lists || [],
            teams: prevData?.me?.teams || [],
          },
        })
      )

      queryClient.setQueryData<OrganizationByAliasQuery>(
        orgKeys.detail(variables.v_alias).queryKey,
        ({ organization } = { organization: [] }) => ({
          organization: produce(organization, (draft) => {
            if (draft[0]?.stats?.follower_count) draft[0].stats.follower_count--
            return draft
          }),
        })
      )

      return {
        snapshot,
      }
    },

    onError(_error, _variables, context) {
      if (context?.snapshot) {
        Object.values(context.snapshot).forEach((item) => {
          queryClient.setQueryData(item.key, item.data)
        })
      }
    },

    onSuccess(response, variables) {
      analytics.track('Organization Unfollow', { id: variables.id })
    },

    onSettled(response, error, variables, context) {
      return Promise.all([
        queryClient.invalidateQueries(userQK.connections()),
        queryClient.invalidateQueries(orgKeys.detail(variables.alias)),
        queryClient.invalidateQueries(needsQK.list({ slug: 'subscribed' })),
      ])
    },
  })
}

function createSnapshot(
  queryClient: QueryClient,
  variables: IdWithAlias
): OrgActionMutationContext {
  return {
    snapshot: {
      connections: {
        key: userQK.connections(),
        data: queryClient.getQueryData<UserConnectionsQuery>(
          userQK.connections()
        ),
      },
      orgDetails: {
        key: orgKeys.detail(variables.v_alias).queryKey,
        data: queryClient.getQueryData<OrganizationByAliasQuery>(
          orgKeys.detail(variables.v_alias).queryKey
        ),
      },
    },
  }
}

export function useUserOrgs() {
  return useUserConnectionsQuery({
    select: (data) =>
      data.me?.orgs.flatMap((item) => (item.org ? [item.org] : [])) ?? [],
  })
}

export function useIsOrgSubscriber(params: { v_alias: string }) {
  const query = useUserConnectionsQuery({
    select: ({ me }) =>
      me?.orgs.some((item) => item.org?.v_alias === params.v_alias),
  })
  return !!query.data
}
